import React from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface ClientFormStepperProps {
  steps: string[];
  currentStep: number;
  setStep: (step: number) => void;
  completedSteps: boolean[];
  billingSubSteps?: { label: string; targetId?: string }[];
  onSubStepClick?: (targetId: string) => void;
  activeSubStepId?: string | null;
  isEditing?: boolean;
  clientName?: string;
}

export const ClientFormStepper: React.FC<ClientFormStepperProps> = ({ steps, currentStep, setStep, completedSteps, billingSubSteps, onSubStepClick, activeSubStepId, isEditing, clientName }) => {
  const getStepDescription = (stepIndex: number) => {
    if (isEditing) {
      switch (stepIndex) {
        case 0: return 'Update basic client information';
        case 1: return 'Modify your roles and rates for this client';
        case 2: return 'Configure rate multipliers & surcharges';
        case 3: return 'Configure billing rules & invoicing';
        case 4: return 'Review changes for this client';
        default: return 'Update client configuration';
      }
    } else {
      switch (stepIndex) {
        case 0: return 'Enter basic client information';
        case 1: return 'Set up your roles and rates for this client';
        case 2: return 'Set up rate multipliers & surcharges';
        case 3: return 'Set up billing rules & invoicing';
        case 4: return 'Review configuration and create client';
        default: return 'Create client profile';
      }
    }
  };
  return (
    <aside className="w-full flex-shrink-0 md:w-48">
      <div className="md:sticky md:top-6">
        {/* Client Name Header */}
        <div className="mb-6 pb-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground break-words">
            {clientName && clientName.trim() ? clientName : (isEditing ? 'Edit Client' : 'New Client')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {getStepDescription(currentStep)}
          </p>
        </div>
        <nav>
        <ol role="list" className="space-y-1">
          {steps.map((stepName, stepIdx) => {
            const isCompleted = completedSteps[stepIdx] && stepIdx < currentStep;
            const isCurrent = stepIdx === currentStep;
            const canNavigate = completedSteps[stepIdx] || isCurrent;

            return (
              <li key={stepName}>
                <button
                  onClick={() => canNavigate && setStep(stepIdx)}
                  disabled={!canNavigate}
                  className={cn(
                    "group flex w-full items-center rounded-md p-2 text-left text-sm font-medium",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
                    !canNavigate ? "cursor-not-allowed" : "cursor-pointer"
                  )}
                >
                  <div
                    className={cn(
                      "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full transition-all",
                      isCompleted ? "bg-primary" : "bg-muted",
                      isCurrent && "ring-2 ring-primary ring-offset-2"
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4 text-white" />
                    ) : (
                      <span className={cn("text-xs", isCurrent ? "text-primary font-bold" : "text-muted-foreground")}>
                        {stepIdx + 1}
                      </span>
                    )}
                  </div>
                  <span
                    className={cn(
                      "ml-3 transition-colors break-words",
                      isCurrent ? "text-primary font-bold" : "text-foreground",
                      !canNavigate && "text-muted-foreground"
                    )}
                  >
                    {stepName}
                  </span>
                </button>
                {stepName === 'Billing & Invoicing' && isCurrent && billingSubSteps && (
                  <ol className="ml-5 mt-2 space-y-2 border-l border-border pl-4">
                    {billingSubSteps.map((subStep, subIndex) => {
                      if (!subStep.targetId) { // It's a header
                        return (
                          <li key={`${subStep.label}-${subIndex}`} className="pt-2 first:pt-0">
                            <p className="text-sm font-semibold text-foreground break-words">{subStep.label}</p>
                          </li>
                        );
                      }
                      const isActive = subStep.targetId === activeSubStepId;
                      return (
                        <li key={subStep.targetId}>
                          <button
                            onClick={() => onSubStepClick?.(subStep.targetId!)}
                            className={cn(
                              "w-full text-left text-sm transition-colors hover:text-foreground break-words",
                              isActive ? "font-semibold text-primary" : "text-muted-foreground"
                            )}
                          >
                            {subStep.label}
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </li>
            );
          })}
        </ol>
        </nav>
      </div>
    </aside>
  );
};
